/**
 * Settings → Sources list: filter, sort and numbers (web.md §4 "Sources list"). Pure functions over the
 * engine's `sources.list()`, `sources.overview()` and `sources.status()`; the state lives in a small
 * store that survives leaving the screen (module state) and a reload (localStorage, best effort).
 *
 * Numbers (engine.md §14 "Where messages end up"): delivered = every message the source ever delivered;
 * per folder = those that are in the folder now (or expired there by retention); deleted = those a
 * person, the assistant or a filter deleted. Percentages are of delivered.
 */

import { useSyncExternalStore } from 'react';
import type { Folder, Source, SourceKind, SourceOverview, SourceStatus } from '@digester/core';

export type SourceStatusFilter = 'enabled' | 'paused' | 'failing';
/** The pseudo-folder key of deleted messages in sort keys and number cells. */
export const DELETED = '__deleted';
/** 'name' | 'received' | 'count:<folderId|__deleted>' | 'share:<folderId|__deleted>'. */
export type SourceSortKey = 'name' | 'received' | `count:${string}` | `share:${string}`;

export interface SourceListState {
  kinds: SourceKind[];
  statuses: SourceStatusFilter[];
  sort: SourceSortKey;
  dir: 'asc' | 'desc';
}

export const DEFAULT_SOURCE_LIST: SourceListState = { kinds: [], statuses: [], sort: 'name', dir: 'asc' };

const STORAGE_KEY = 'digester.sources-list.v1';
const KINDS: readonly SourceKind[] = ['rss', 'email', 'youtube', 'github', 'telegram', 'mastodon', 'bluesky', 'reddit', 'discourse', 'watcher'];
const STATUSES: readonly SourceStatusFilter[] = ['enabled', 'paused', 'failing'];

/** A stored state, checked field by field (anything unknown falls back to the default). */
export function parseSourceListState(value: unknown): SourceListState {
  if (typeof value !== 'object' || value === null) return DEFAULT_SOURCE_LIST;
  const v = value as Record<string, unknown>;
  const kinds = Array.isArray(v.kinds) ? v.kinds.filter((k): k is SourceKind => KINDS.includes(k as SourceKind)) : [];
  const statuses = Array.isArray(v.statuses) ? v.statuses.filter((s): s is SourceStatusFilter => STATUSES.includes(s as SourceStatusFilter)) : [];
  const sort = typeof v.sort === 'string' && /^(name|received|(count|share):[a-z0-9_]{1,64})$/.test(v.sort) ? (v.sort as SourceSortKey) : 'name';
  const dir = v.dir === 'desc' ? 'desc' : 'asc';
  return { kinds, statuses, sort, dir };
}

function load(): SourceListState {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return raw ? parseSourceListState(JSON.parse(raw)) : DEFAULT_SOURCE_LIST;
  } catch {
    return DEFAULT_SOURCE_LIST;
  }
}

let state: SourceListState | null = null;
const listeners = new Set<() => void>();
const current = (): SourceListState => (state ??= load());

export const sourceList = {
  get: current,
  set(patch: Partial<SourceListState>): void {
    state = { ...current(), ...patch };
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Private windows / blocked storage: the state still lives for this session.
    }
    for (const l of [...listeners]) l();
  },
  reset(): void {
    sourceList.set(DEFAULT_SOURCE_LIST);
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function useSourceList(): SourceListState {
  return useSyncExternalStore(sourceList.subscribe, current, current);
}

export function isFiltered(s: SourceListState): boolean {
  return s.kinds.length > 0 || s.statuses.length > 0;
}

// ── Rows ───────────────────────────────────────────────────────────────────────────────────────

export type SourceHealth = 'ok' | 'error' | 'secret' | 'paused';

export function healthOf(source: Source, status: SourceStatus | undefined): SourceHealth {
  if (!source.enabled) return 'paused';
  if (status?.needsSecret) return 'secret';
  if (status?.lastError) return 'error';
  return 'ok';
}

export interface SourceListRow {
  source: Source;
  health: SourceHealth;
  received: number;
  /** folder id (and DELETED) → count. */
  counts: Record<string, number>;
}

export function countOf(row: SourceListRow, key: string): number {
  return row.counts[key] ?? 0;
}

/** Share of delivered in [0, 1]; null when nothing was delivered (sorts last either way). */
export function shareOf(row: SourceListRow, key: string): number | null {
  return row.received === 0 ? null : countOf(row, key) / row.received;
}

/** Whole percent for display: "0%" only for a true zero, "<1%" for a small non-zero share. */
export function percentText(count: number, received: number): string {
  if (received === 0) return '—';
  if (count === 0) return '0%';
  const p = (count / received) * 100;
  return p < 1 ? '<1%' : `${Math.round(p)}%`;
}

function matchesStatus(row: SourceListRow, statuses: readonly SourceStatusFilter[]): boolean {
  if (statuses.length === 0) return true;
  return statuses.some((s) => (s === 'paused' ? row.health === 'paused' : s === 'failing' ? row.health === 'error' || row.health === 'secret' : row.health !== 'paused'));
}

const byName = (a: SourceListRow, b: SourceListRow): number => a.source.name.localeCompare(b.source.name, undefined, { sensitivity: 'base' });

/** The value a row sorts by; null = no data (always after the rows with data). */
function sortValue(row: SourceListRow, key: SourceSortKey): number | null {
  if (key === 'received') return row.received;
  if (key.startsWith('count:')) return countOf(row, key.slice(6));
  if (key.startsWith('share:')) return shareOf(row, key.slice(6));
  return null;
}

export function buildRows(sources: readonly Source[], overview: readonly SourceOverview[], statuses: readonly SourceStatus[]): SourceListRow[] {
  const numbers = new Map(overview.map((o) => [o.sourceId, o]));
  const status = new Map(statuses.map((s) => [s.sourceId, s]));
  return sources.map((source) => {
    const o = numbers.get(source.id);
    return {
      source,
      health: healthOf(source, status.get(source.id)),
      received: o?.received ?? 0,
      counts: { ...(o?.folders ?? {}), [DELETED]: o?.deleted ?? 0 },
    };
  });
}

/** Filter by kind, status and the search text, then sort (ties by name). */
export function arrangeRows(rows: readonly SourceListRow[], s: SourceListState, search: string): SourceListRow[] {
  const q = search.trim().toLowerCase();
  const shown = rows.filter(
    (r) => (q === '' || r.source.name.toLowerCase().includes(q)) && (s.kinds.length === 0 || s.kinds.includes(r.source.kind)) && matchesStatus(r, s.statuses),
  );
  const sign = s.dir === 'asc' ? 1 : -1;
  return shown.sort((a, b) => {
    if (s.sort === 'name') return sign * byName(a, b);
    const va = sortValue(a, s.sort);
    const vb = sortValue(b, s.sort);
    if (va === null && vb === null) return byName(a, b);
    if (va === null) return 1;
    if (vb === null) return -1;
    return va === vb ? byName(a, b) : sign * (va - vb);
  });
}

/** The folders shown in rows and offered for sorting: the built-ins and every custom folder, in sidebar order. */
export function numberFolders(folders: readonly Folder[]): Array<{ key: string; name: string; icon: string }> {
  return [...folders].sort((a, b) => a.position - b.position).map((f) => ({ key: f.id as string, name: f.name, icon: f.icon }));
}

export function sortOptions(folders: readonly Folder[]): Array<readonly [SourceSortKey, string]> {
  const cells = [...numberFolders(folders), { key: DELETED, name: 'Deleted', icon: 'trash' }];
  return [
    ['name', 'Name'],
    ['received', 'Delivered'],
    ...cells.map((c) => [`count:${c.key}` as SourceSortKey, c.key === DELETED ? 'Deleted' : `In ${c.name}`] as const),
    ...cells.map((c) => [`share:${c.key}` as SourceSortKey, c.key === DELETED ? '% deleted' : `% in ${c.name}`] as const),
  ];
}

export function sortLabel(key: SourceSortKey, folders: readonly Folder[]): string {
  return sortOptions(folders).find(([k]) => k === key)?.[1] ?? 'Name';
}
